import axios from 'axios';
import { API_BASE_URL } from '../components/config/api';
import { addClientBuildHeader } from '../utils/clientBuild';

// API του support panel (/api/support). Ίδιο token/interceptors με το adminApi.
// Τα billing endpoints του support περνάνε από το adminApi (ίδια routes με τον
// superadmin, με χαλαρωμένο gate στα GETs + πληρωμές).
const supportApi = axios.create({
  baseURL: `${API_BASE_URL}/api/support`,
  headers: { 'Content-Type': 'application/json' },
});

supportApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('xentrix_token');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return addClientBuildHeader(config);
  },
  (error) => Promise.reject(error)
);

supportApi.interceptors.response.use(
  (response) => response.data,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('xentrix_token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const supportAPI = {
  // Καταστήματα (επιμελημένη λίστα: επικοινωνία, AnyDesk, flags, tickets)
  getStores: () => supportApi.get('/stores'),
  getAgentStatus: (storeId: number) => supportApi.get(`/stores/${storeId}/agent-status`),

  // Συσκευές ταμείου: οθόνη πελάτη + αναγνώριση κλήσης ανά PC (agent 2.4.0+). null = «από το .env».
  getDevices: (storeId: number) => supportApi.get(`/stores/${storeId}/devices`),
  saveDevices: (storeId: number, uid: string, data: { display: unknown; callerId: unknown }) =>
    supportApi.put(`/stores/${storeId}/devices/${uid}`, data),
  deviceCmd: (storeId: number, uid: string, op: string, args: Record<string, unknown> = {}) =>
    supportApi.post(`/stores/${storeId}/devices/${uid}/cmd`, { ...args, op }),
  getLastModemCall: (storeId: number, since: string) =>
    supportApi.get(`/stores/${storeId}/devices/last-call`, { params: { since } }),

  // Κάρτα καταστήματος (25/10/2026): agent ανά PC, επιδόσεις εκτυπωτών, πρόσβαση, παραστατικά που κόλλησαν.
  getAgent: (storeId: number) => supportApi.get(`/stores/${storeId}/agent`),
  getAccess: (storeId: number) => supportApi.get(`/stores/${storeId}/access`),
  resetStorePassword: (storeId: number, newPassword: string) =>
    supportApi.post(`/stores/${storeId}/reset-password`, { new_password: newPassword }),
  resetEmployeePin: (storeId: number, employeeId: number, newPin: string) =>
    supportApi.post(`/stores/${storeId}/employees/${employeeId}/reset-pin`, { new_pin: newPin }),
  getFiscalPending: (storeId: number) => supportApi.get(`/stores/${storeId}/fiscal-pending`),
  retryFiscalDocument: (storeId: number, documentId: number) =>
    supportApi.post(`/stores/${storeId}/fiscal-documents/${documentId}/retry`),

  // Νέο κατάστημα (25/10/2026): αλυσίδες για τον οδηγό, δημιουργία (κατάστημα + διαχειριστής + αλυσίδα + Oxygen σε μία
  // συναλλαγή), λίστα ελέγχου «Στήσιμο» και «Ημερολόγιο» ενεργειών support + super admin.
  getOrganizations: () => supportApi.get('/organizations'),
  createStore: (data: Record<string, unknown>) => supportApi.post('/stores', data),
  getSetup: (storeId: number) => supportApi.get(`/stores/${storeId}/setup`),
  getAudit: (params?: { store_id?: number; employee_id?: number; action?: string; before?: number; limit?: number }) =>
    supportApi.get('/audit', { params }),

  // «Τι θέλει προσοχή» (26/10/2026): ό,τι σπάει τώρα, σε όλα τα καταστήματα.
  getAttention: () => supportApi.get('/attention'),

  // «Στόλος» (25/10/2026): εκδόσεις agent + εκτύπωση όλων· στόχος έκδοσης για ΕΝΑ κατάστημα.
  getFleet: (days = 7) => supportApi.get('/fleet', { params: { days } }),
  setAgentTarget: (storeId: number, version: string) => supportApi.post(`/stores/${storeId}/agent-target`, { version }),
  clearAgentTarget: (storeId: number) => supportApi.post(`/stores/${storeId}/agent-target/clear`),

  // «Πλατφόρμες» (25/10/2026): efood / Wolt / BOX ανά κατάστημα — με τα μυστικά (απόφαση χρήστη).
  getPlatforms: (storeId: number) => supportApi.get(`/stores/${storeId}/platforms`),
  savePlatform: (storeId: number, platform: string, data: Record<string, unknown>) =>
    supportApi.put(`/stores/${storeId}/platforms/${platform}`, data),
  disconnectPlatform: (storeId: number, platform: string) => supportApi.delete(`/stores/${storeId}/platforms/${platform}`),

  // «Αλυσίδες» (25/10/2026): καταστήματα + χρήστες οργανισμών — μόνο διαχειριστής / μόνο προβολή, ποτέ owner.
  getOrganization: (orgId: number) => supportApi.get(`/organizations/${orgId}`),
  addOrgStore: (orgId: number, storeId: number, priceListId: number | null) =>
    supportApi.post(`/organizations/${orgId}/stores`, { store_id: storeId, price_list_id: priceListId }),
  removeOrgStore: (orgId: number, storeId: number) => supportApi.delete(`/organizations/${orgId}/stores/${storeId}`),
  setOrgStoreLabel: (orgId: number, storeId: number, displayName: string) =>
    supportApi.put(`/organizations/${orgId}/stores/${storeId}`, { display_name: displayName }),
  setOrgStorePriceList: (orgId: number, storeId: number, priceListId: number | null) =>
    supportApi.put(`/organizations/${orgId}/stores/${storeId}/price-list`, { price_list_id: priceListId }),
  createOrgMember: (orgId: number, data: Record<string, unknown>) => supportApi.post(`/organizations/${orgId}/members/new`, data),
  addOrgMember: (orgId: number, data: { org_user_id: number; role: string; store_ids: number[] | null }) =>
    supportApi.post(`/organizations/${orgId}/members`, data),
  updateOrgMember: (orgId: number, orgUserId: number, data: { role: string; store_ids?: number[] | null }) =>
    supportApi.put(`/organizations/${orgId}/members/${orgUserId}`, data),
  removeOrgMember: (orgId: number, orgUserId: number) => supportApi.delete(`/organizations/${orgId}/members/${orgUserId}`),
  resetOrgUserPassword: (orgUserId: number, newPassword: string) =>
    supportApi.post(`/org-users/${orgUserId}/reset-password`, { new_password: newPassword }),

  // Παραγγελίες καταστήματος + ακύρωση
  getStoreOrders: (storeId: number, params?: { from?: string; to?: string; status?: string }) =>
    supportApi.get(`/stores/${storeId}/orders`, { params }),
  cancelOrder: (storeId: number, orderId: number, data: { reason: string; force?: boolean; cancelFiscal?: boolean }) =>
    supportApi.post(`/stores/${storeId}/orders/${orderId}/cancel`, data),

  // Tickets
  getTickets: (params?: { status?: string; store_id?: number; type?: string; q?: string }) =>
    supportApi.get('/tickets', { params }),
  getTicket: (ticketId: number) => supportApi.get(`/tickets/${ticketId}`),
  createTicket: (data: { store_id: number; type?: string; priority?: string; title: string; description?: string }) =>
    supportApi.post('/tickets', data),
  updateTicket: (ticketId: number, data: any) => supportApi.put(`/tickets/${ticketId}`, data),
  addTicketComment: (ticketId: number, body: string) => supportApi.post(`/tickets/${ticketId}/comments`, { body }),
};

export default supportApi;
